<template>
  <v-container class="py-8">
    <template v-if="loaded">
      <DashboardV2
        :group="group"
        :group-schema="groupSchema"
        data-testid="dashboard-v2"
      />

      <DecisionDialog
        @confirm="goToSurvey"
        ref="appSubmissionSurveyDialog"
        :content="surveySchema?.description"
        cancel-button-text="Skip"
        confirm-button-text="Continue"
        title="Application successfully submitted"
      />
    </template>
  </v-container>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import DashboardV2 from '@/parent/components/dashboards/DashboardV2.vue';
import DecisionDialog from '@/shared/components/DecisionDialog.vue';
import useApi from '@/shared/composables/useApi';

const { api: Api } = useApi();
const route = useRoute();
const store = useStore();

const group = ref(null);
const groupSchema = ref(null);
const loaded = ref(false);
const surveySchema = ref(null);
const appSubmissionSurveyDialog = ref(null);

const emit = defineEmits(['replace']);

async function followPendingRedirect() {
  const pendingRedirect = JSON.parse(window.localStorage.redirect);
  window.localStorage.removeItem('redirect');
  emit('replace', pendingRedirect);
}

async function goToSurvey() {
  emit('replace', {
    name: 'AnonymousSurveyShow',
    params: { schemaId: surveySchema.value.id },
  });
}

async function load() {
  await loadGroup();
  loaded.value = true;
}

async function loadGroup() {
  if (!store.state.profile?.group_id) return;

  const resp = await Api.parent.group.get(store.state.profile.group_id);
  if (resp?.status !== 200) return;

  group.value = resp.data;
  groupSchema.value = store.state.schemas[group.value.schema_id];
}

async function loadSurveySchema() {
  const resp = await Api.publicApi.organization.schema.get(route.query.submittedSurveySchemaId);

  if (!resp) return;
  surveySchema.value = resp.data;
  appSubmissionSurveyDialog.value.open();
}

function redirectToLanding() {
  emit('replace', { name: 'Landing' });
}

async function redirectMember() {
  if (store.state.pages.Search.enabled && store.state.profile.search_id) {
    emit('replace', { name: 'SearchShow', params: { searchId: store.state.profile.search_id } });
  } else {
    emit('replace', { name: 'Landing' });
  }
}

async function redirectOrLoad() {
  if (await shouldRedirectToLanding()) return redirectToLanding();
  if (!store.state.pages.Dashboard.enabled) return redirectMember();
  if (window.localStorage.redirect) return followPendingRedirect();

  if (store.state.is_anonymous) {
    store.dispatch('identify', {
      async success() {
        await load();
      },
      failure() {
        emit('replace', { name: 'Signup' });
      },
    });
  } else {
    await load();
  }
}

async function shouldRedirectToLanding() {
  const response = await Api.parent.pageSettings.dashboard();
  return response?.data?.redirect;
}

onMounted(redirectOrLoad);

watchEffect(async () => {
  if (appSubmissionSurveyDialog.value) {
    if (route.query.submittedSurveySchemaId) {
      await loadSurveySchema();
    }
  }
});
</script>
